import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BookingModal from '../booking/BookingModal';
import BookingSuccess from '../booking/BookingSuccess';
import { navigateToMainPageSection } from '../../utils/scrollUtils';
import './WorkshopDetails.css';
import { useTranslation } from 'react-i18next';

const WorkshopDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [workshopImages, setWorkshopImages] = useState([]);
  const [imageOrientations, setImageOrientations] = useState([]);
  const [parsedBenefits, setParsedBenefits] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [galleryImageIndex, setGalleryImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [instructorBioData, setInstructorBioData] = useState('');
  const [bioExpanded, setBioExpanded] = useState(false);
  const BIO_CHARACTER_LIMIT = 300;
  
  // Language detection
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';

  // Get current URL for sharing
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Check if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Fetch instructor bio separately using the same API call pattern as InstructorProfile.js
  useEffect(() => {
    const fetchInstructorBio = async () => {
      if (!workshop || !workshop.instructor_id) return;
      
      try {
        // Determine if we're on production or local
        const isProd = window.location.hostname === 'www.tideasolis.com' || window.location.hostname === 'tideasolis.com';
        
        // Create API URL based on environment
        let apiUrl = `/api/instructors/${workshop.instructor_id}`;
        if (isProd) {
          // Use absolute URL on production
          apiUrl = `https://${window.location.hostname}/api/instructors/${workshop.instructor_id}`;
        }
        
        const response = await axios.get(apiUrl, { 
          headers: { 'Accept': 'application/json' }
        });
        
        if (response.data && response.data.bio) {
          setInstructorBioData(response.data.bio);
        }
      } catch (err) {
        console.error('Error fetching instructor bio:', err);
        // Silently fail - we'll use the fallback text
      }
    };
    
    fetchInstructorBio();
  }, [workshop]);

  useEffect(() => {
    const fetchWorkshopDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`workshops/${id}`);
        // console.log('Workshop data:', response.data); // Log the data to see what's coming from the API
        setWorkshop(response.data);
        
        // Process benefits if they exist
        if (response.data.benefits) {
          try {
            // console.log('Raw benefits data:', response.data.benefits);
            // Check if benefits is already an array
            if (Array.isArray(response.data.benefits)) {
              setParsedBenefits(response.data.benefits);
            } else {
              // Try to parse as JSON if it's a string
              const benefitsArray = JSON.parse(response.data.benefits);
              if (Array.isArray(benefitsArray)) {
                setParsedBenefits(benefitsArray);
              } else {
                // If not an array, create a single-item array
                setParsedBenefits([response.data.benefits]);
              }
            }
          } catch (e) {
            // console.error('Error parsing benefits:', e);
            // If parsing fails, treat it as a string that might be comma separated
            if (typeof response.data.benefits === 'string') {
              const benefitsArray = response.data.benefits
                .split(',')
                .map(item => item.trim())
                .filter(item => item.length > 0);
              
              if (benefitsArray.length > 0) {
                setParsedBenefits(benefitsArray);
              } else {
                // Fallback to default benefits
                setParsedBenefits([
                  t('workshop_details.default_benefit1'),
                  t('workshop_details.default_benefit2'),
                  t('workshop_details.default_benefit3'),
                  t('workshop_details.default_benefit4'),
                  t('workshop_details.default_benefit5')
                ]);
              }
            } else {
              // Fallback to default benefits
              setParsedBenefits([
                t('workshop_details.default_benefit1'),
                t('workshop_details.default_benefit2'),
                t('workshop_details.default_benefit3'),
                t('workshop_details.default_benefit4'),
                t('workshop_details.default_benefit5')
              ]);
            }
          }
        } else {
          // Use default benefits if none provided
          setParsedBenefits([
            t('workshop_details.default_benefit1'),
            t('workshop_details.default_benefit2'),
            t('workshop_details.default_benefit3'),
            t('workshop_details.default_benefit4'),
            t('workshop_details.default_benefit5')
          ]);
        }
        
        // Process image URLs
        const imagesArray = [];
        if (response.data.image_url) {
          // Check if image_url is an array or a string
          if (Array.isArray(response.data.image_url)) {
            imagesArray.push(...response.data.image_url);
          } else if (typeof response.data.image_url === 'string') {
            try {
              // Check if it's a JSON string that contains an array
              const parsedImages = JSON.parse(response.data.image_url);
              if (Array.isArray(parsedImages)) {
                imagesArray.push(...parsedImages);
              } else {
                imagesArray.push(response.data.image_url);
              }
            } catch (e) {
              // If it's not a valid JSON string, treat it as a regular URL
              imagesArray.push(response.data.image_url);
            }
          }
        }
        
        // Use a default image if no images were found
        if (imagesArray.length === 0) {
          imagesArray.push('https://images.unsplash.com/photo-1603988363607-e1e4a66962c6?q=80&w=2070&auto=format&fit=crop');
        }
        
        setWorkshopImages(imagesArray);
        
        // Initialize image orientations array with 'landscape' as default
        setImageOrientations(new Array(imagesArray.length).fill('landscape'));
        
        // Check image orientations after images have loaded
        imagesArray.forEach((src, index) => {
          const img = new Image();
          img.onload = () => {
            setImageOrientations(prevOrientations => {
              const newOrientations = [...prevOrientations];
              newOrientations[index] = img.width >= img.height ? 'landscape' : 'portrait';
              return newOrientations;
            });
          };
          img.src = src;
        });
        
        setError(null);
      } catch (err) {
        // console.error('Error fetching workshop details:', err);
        setError(t('workshop_details.failed_load_workshop'));
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshopDetails();
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [id, t]);
  
  // Add SEO metadata for Slovak language users
  useEffect(() => {
    if (workshop && isSlovak) {
      // Set page title for Workshop Details page
      const pageTitle = `${workshop.title} | Workshop | TiDeaSolis Yoga �t�dio`;
      document.title = pageTitle;
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      const description = workshop.description || t('workshop_details.workshop_default_description');
      metaDescription.setAttribute('content', description.substring(0, 160) + '...');
      
      // Add Open Graph tags
      const updateMetaTag = (property, content) => {
        let metaTag = document.querySelector(`meta[property="${property}"]`);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('property', property);
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', content);
      };
      
      updateMetaTag('og:locale', 'sk_SK');
      updateMetaTag('og:title', pageTitle);
      updateMetaTag('og:description', description.substring(0, 160) + '...');
      updateMetaTag('og:type', 'event');
      updateMetaTag('og:url', currentUrl);
      
      if (workshopImages.length > 0) {
        updateMetaTag('og:image', workshopImages[0]);
      }
      
      // Add schema.org structured data for the workshop
      const startDate = new Date(workshop.start_time);
      const endDate = new Date(startDate);
      if (workshop.duration) {
        endDate.setMinutes(endDate.getMinutes() + (workshop.duration * 60));
      } else {
        endDate.setHours(endDate.getHours() + 2); // Default 2-hour duration
      }
      
      const eventData = {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": workshop.title,
        "description": description,
        "startDate": startDate.toISOString(),
        "endDate": endDate.toISOString(),
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": workshop.location || "TiDeaSolis Yoga �t�dio",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "�af�rikova 37",
            "addressLocality": "Ro��ava",
            "addressCountry": "SK"
          }
        },
        "image": workshopImages.length > 0 ? workshopImages : undefined,
        "performer": {
          "@type": "Person",
          "name": workshop.instructor,
          "image": workshop.instructor_image
        },
        "organizer": {
          "@type": "Organization",
          "name": "TiDeaSolis Yoga �t�dio",
          "url": window.location.origin
        },
        "offers": {
          "@type": "Offer",
          "price": workshop.price || 0,
          "priceCurrency": "EUR",
          "availability": workshop.available_spots > 0 ? 
            "https://schema.org/InStock" : 
            "https://schema.org/SoldOut",
          "validFrom": new Date().toISOString()
        },
        "url": currentUrl
      };
      
      // Add or update JSON-LD script
      let scriptTag = document.getElementById('workshop-schema');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'workshop-schema';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(eventData);
    }
    
    return () => {
      // Optional cleanup
      const script = document.getElementById('workshop-schema');
      if (script) {
        script.remove();
      }
    };
  }, [workshop, isSlovak, workshopImages, currentUrl, t]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    return date.toLocaleDateString(t('workshop_details.date_locale'), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    return date.toLocaleTimeString(t('workshop_details.date_locale'), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleThumbnailClick = (index) => {
    // Always update the active image in the hero section
    setActiveImageIndex(index);
    
    // Open the gallery modal for both mobile and desktop users
    setGalleryImageIndex(index);
    setShowFullImage(true);
  };

  const handleBookNowClick = () => {
    setShowBookingModal(true);
  };

  const handleBookingClose = () => {
    setShowBookingModal(false);
  };

  const handleBookingSuccess = (data) => {
    setBookingData(data);
    setShowBookingModal(false);
    setBookingSuccess(true);
  };

  const handleSuccessClose = () => {
    setBookingSuccess(false);
    setBookingData(null);
  };

  // Toggle fullscreen image view
  const toggleFullImage = () => {
    setGalleryImageIndex(activeImageIndex);
    setShowFullImage(!showFullImage);
  };
  
  // Gallery navigation
  const showNextImage = (e) => {
    e.stopPropagation();
    setGalleryImageIndex((prevIndex) => 
      prevIndex === workshopImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const showPrevImage = (e) => {
    e.stopPropagation();
    setGalleryImageIndex((prevIndex) => 
      prevIndex === 0 ? workshopImages.length - 1 : prevIndex - 1
    );
  };

  // Close fullscreen view on escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showFullImage) {
        setShowFullImage(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [showFullImage]);

  // Function to handle back navigation with reliable scrolling
  const handleBackToWorkshops = (e) => {
    e.preventDefault();
    
    // Store target position in sessionStorage instead of localStorage
    // This is more appropriate for navigation within the same browser session
    sessionStorage.setItem('scrollToPosition', '2333.837646484375');
    sessionStorage.setItem('scrollTarget', 'workshops');
    sessionStorage.setItem('scrollTimestamp', Date.now().toString());
    
    // Navigate to homepage
    window.location.href = '/';
    
    // No need for additional code here as the navigation will reload the page
  };

  // Social media sharing functions
  const shareOnFacebook = () => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const shareText = workshop ? `${t('workshop_details.check_out_workshop')}: ${workshop.title}` : t('workshop_details.check_out_workshop');
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(currentUrl)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = () => {
    const shareText = workshop ? `${t('workshop_details.check_out_workshop')}: ${workshop.title} ${currentUrl}` : `${t('workshop_details.check_out_workshop')} ${currentUrl}`;
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank');
  };

  const shareViaEmail = () => {
    const subject = workshop ? `${t('workshop_details.join_me_at')} ${workshop.title} - ${t('workshop_details.workshop')}` : `${t('workshop_details.join_me_at')} ${t('workshop_details.workshop')}`;
    const body = workshop 
      ? `${t('workshop_details.thought_might_interest')}:\n\n${workshop.title}\n\n${t('workshop_details.date')}: ${formatDate(workshop.start_time)}\n${t('workshop_details.time')}: ${formatTime(workshop.start_time)}\n${t('workshop_details.location')}: ${workshop.location || t('workshop_details.main_studio')}\n\n${t('workshop_details.find_out_more')}: ${currentUrl}`
      : `${t('workshop_details.thought_might_interest')}. ${t('workshop_details.check_it_out')}: ${currentUrl}`;
    
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="workshop-details-page" lang={currentLanguage}>
        <div className="details-loading-container">
          <div className="details-glowing-loader"></div>
          <p>{t('workshop_details.loading_workshop_details')}</p>
        </div>
      </div>
    );
  }

  if (error || !workshop) {
    return (
      <div className="workshop-details-page" lang={currentLanguage}>
        <div className="details-error-container">
          <div className="details-error-icon">!</div>
          <h2>{t('workshop_details.workshop_not_found')}</h2>
          <p>{error || t('workshop_details.workshop_not_found_message')}</p>
          <Link 
            to="/#workshops" 
            className="details-back-button" 
            onClick={handleBackToWorkshops} 
            lang={currentLanguage}
            aria-label={isSlovak ? "N�vrat na str�nku workshopov" : "Return to workshops page"}
          >
            {t('workshop_details.return_to_workshops')}
          </Link>
        </div>
      </div>
    );
  }

  const {
    title,
    description,
    start_time,
    end_time,
    duration,
    location,
    price,
    max_capacity,
    available_spots,
    instructor,
    instructor_id,
    instructor_image,
    instructor_bio,
    difficulty,
    prepayment
  } = workshop;

  // Prepare class data for booking modal
  const bookingClass = {
    id: workshop.id,
    class_name: title,
    title: title,
    date_time: start_time,
    start_time: start_time,
    end_time: end_time,
    location: location,
    available_spots: available_spots,
    max_capacity: max_capacity,
    instructor: instructor,
    instructor_id: instructor_id
  };

  return (
    <div className="workshop-details-page" lang={currentLanguage} itemScope itemType="https://schema.org/Event">
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description || t('workshop_details.workshop_default_description')} />
      <meta itemProp="startDate" content={start_time} />
      <meta itemProp="location" content={location || t('workshop_details.main_studio')} />
      
      {/* Back Arrow */}
      <div className="details-back-arrow-container">
        <Link 
          to="/#workshops" 
          className="details-back-arrow" 
          onClick={handleBackToWorkshops}
          lang={currentLanguage}
          aria-label={isSlovak ? "N�vrat na str�nku workshopov" : "Return to workshops page"}
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
          <span>{t('workshop_details.back_to_workshops')}</span>
        </Link>
      </div>
      
      {/* Improved Hero Section */}
      <section className="details-hero-section">
        <div className="image-fit-container">
        <img 
          src={workshopImages[activeImageIndex]} 
          alt={title} 
            className={`details-hero-image ${imageOrientations[activeImageIndex] || 'landscape'}`}
            itemProp="image"
        />
        </div>
        <div className="details-hero-overlay"></div>
        
        {/* Fullscreen view button */}
        <div className="full-image-view" onClick={toggleFullImage} title={t('workshop_details.view_full_image')}>
          <i className="fa fa-expand" aria-hidden="true"></i>
        </div>
        
        <div className="details-hero-content">
          <div className="details-hero-title-container">
            <h1 className="details-hero-title" itemProp="name">{title}</h1>
          </div>
        </div>
      </section>

      {/* Gallery Modal with Navigation Arrows */}
      {showFullImage && (
        <div className="gallery-modal" onClick={() => setShowFullImage(false)}>
          <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={workshopImages[galleryImageIndex]} 
              alt={`${title} - ${galleryImageIndex + 1}`} 
              className={`gallery-image ${imageOrientations[galleryImageIndex] || 'landscape'}`}
              onTouchStart={(e) => {
                setTouchStartX(e.touches[0].clientX);
              }}
              onTouchEnd={(e) => {
                if (touchStartX === null) return;
                
                const touchEndX = e.changedTouches[0].clientX;
                const diffX = touchEndX - touchStartX;
                
                // If swipe distance is significant (more than 50px)
                if (Math.abs(diffX) > 50) {
                  if (diffX > 0) {
                    // Swipe right -> go to previous image
                    showPrevImage(e);
                  } else {
                    // Swipe left -> go to next image
                    showNextImage(e);
                  }
                }
                
                setTouchStartX(null);
              }}
            />
            
            <button className="gallery-close-btn" onClick={() => setShowFullImage(false)}>
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
            
            {workshopImages.length > 1 && (
              <>
                <button className="gallery-nav-btn gallery-prev-btn" onClick={showPrevImage}>
                  <i className="fa fa-chevron-left" aria-hidden="true"></i>
                </button>
                <button className="gallery-nav-btn gallery-next-btn" onClick={showNextImage}>
                  <i className="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
                <div className="gallery-counter">
                  {galleryImageIndex + 1} / {workshopImages.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Gallery Thumbnails */}
      {workshopImages.length > 1 && (
        <div className="details-gallery-container">
          <div className="details-gallery-thumbnails-container">
            <div className="details-gallery-thumbnails">
              {workshopImages.map((image, index) => (
                <div 
                  key={index} 
                  className={`details-gallery-thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                  onClick={() => handleThumbnailClick(index)}
                  role="button"
                  aria-label={isSlovak ? `Zobrazi� obr�zok ${index + 1}` : `View image ${index + 1}`}
                  tabIndex={0}
                >
                  <img src={image} alt={`${title} ${t('workshop_details.thumbnail')} ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="workshop-details-container">
        {/* Main Content */}
        <div className="workshop-details-content">
          <div className="details-left-ellipse details-decoration"></div>
          <div className="details-right-ellipse details-decoration"></div>

          {/* Tabs Navigation */}
          <div className="details-tabs-container">
            <div className="details-tabs-navigation" role="tablist">
              <button 
                className={`details-tab-button ${activeTab === 'description' ? 'active' : ''}`}
                onClick={() => setActiveTab('description')}
                role="tab"
                aria-selected={activeTab === 'description' ? 'true' : 'false'}
                id="tab-description"
                lang={currentLanguage}
              >
                {t('workshop_details.tab_description')}
              </button>
              <button 
                className={`details-tab-button ${activeTab === 'what-to-bring' ? 'active' : ''}`}
                onClick={() => setActiveTab('what-to-bring')}
                role="tab"
                aria-selected={activeTab === 'what-to-bring' ? 'true' : 'false'}
                id="tab-what-to-bring"
                lang={currentLanguage}
              >
                {t('workshop_details.tab_what_to_bring')}
              </button>
              <button 
                className={`details-tab-button ${activeTab === 'instructor' ? 'active' : ''}`}
                onClick={() => setActiveTab('instructor')}
                role="tab"
                aria-selected={activeTab === 'instructor' ? 'true' : 'false'}
                id="tab-instructor"
                lang={currentLanguage}
              >
                {t('workshop_details.tab_about_instructor')}
              </button>
            </div>

            {/* Tab Content */}
            <div className="details-tab-content">
              {activeTab === 'description' && (
                <div 
                  className="description-tab" 
                  role="tabpanel" 
                  aria-labelledby="tab-description"
                  itemProp="description"
                >
                  <h2>{t('workshop_details.about_this_workshop')}</h2>
                  <p className="formatted-description" style={{ whiteSpace: 'pre-wrap' }}>
                    {description || t('workshop_details.workshop_default_description')}
                  </p>
                  
                  <div className="details-benefits-section">
                    <h3>{t('workshop_details.benefits')}</h3>
                    <ul className="details-benefits-list">
                      {parsedBenefits.map((benefit, index) => (
                        <li key={index} className="benefits-list-item" lang={currentLanguage}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'what-to-bring' && (
                <div 
                  className="what-to-bring-tab" 
                  role="tabpanel" 
                  aria-labelledby="tab-what-to-bring"
                >
                  <h2>{t('workshop_details.tab_what_to_bring')}</h2>
                  <div className="details-items-grid">
                    <div className="details-item">
                      <div className="details-item-icon">
                        <i className="fa fa-tshirt" aria-hidden="true"></i>
                      </div>
                      <h3 lang={currentLanguage}>{t('workshop_details.what_to_bring_item1_title')}</h3>
                      <p lang={currentLanguage}>{t('workshop_details.what_to_bring_item1_desc')}</p>
                    </div>
                    <div className="details-item">
                      <div className="details-item-icon">
                        <i className="fa fa-glass-water" aria-hidden="true"></i>
                      </div>
                      <h3 lang={currentLanguage}>{t('workshop_details.what_to_bring_item2_title')}</h3>
                      <p lang={currentLanguage}>{t('workshop_details.what_to_bring_item2_desc')}</p>
                    </div>
                    <div className="details-item">
                      <div className="details-item-icon">
                        <i className="fa fa-hand-holding-droplet" aria-hidden="true"></i>
                      </div>
                      <h3 lang={currentLanguage}>{t('workshop_details.what_to_bring_item3_title')}</h3>
                      <p lang={currentLanguage}>{t('workshop_details.what_to_bring_item3_desc')}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'instructor' && (
                <div className="instructor-tab" role="tabpanel" aria-labelledby="tab-instructor">
                  <div className="details-instructor-profile" itemProp="performer" itemScope itemType="https://schema.org/Person">
                    <div className="details-instructor-image-container">
                      {instructor_image && <img src={instructor_image} alt={instructor} itemProp="image" />}
                    </div>
                    <div className="details-instructor-info">
                      <h2 itemProp="name">{instructor}</h2>
                      <div className="details-instructor-bio-container">
                        <p className="details-instructor-bio formatted-bio" itemProp="description" lang={currentLanguage} style={{ whiteSpace: 'pre-wrap' }}>
                          {(() => {
                            const bioText = instructorBioData || instructor_bio || t('workshop_details.instructor_default_bio');
                            
                            if (bioText.length <= BIO_CHARACTER_LIMIT) {
                              return bioText;
                            }
                            
                            return bioExpanded ? bioText : `${bioText.substring(0, BIO_CHARACTER_LIMIT)}...`;
                          })()}
                        </p>
                        
                        {(instructorBioData || instructor_bio) && (instructorBioData.length > BIO_CHARACTER_LIMIT || (instructor_bio && instructor_bio.length > BIO_CHARACTER_LIMIT)) && (
                          <button 
                            className="bio-toggle-button" 
                            onClick={() => setBioExpanded(!bioExpanded)}
                            aria-expanded={bioExpanded}
                          >
                            {bioExpanded ? t('workshop_details.read_less', 'Menej') : t('workshop_details.read_more', 'Viac')}
                          </button>
                        )}
                      </div>
                      <Link 
                        to={`/instructors/${instructor_id}`} 
                        className="details-view-profile-button"
                        lang={currentLanguage}
                      >
                        {t('workshop_details.view_full_profile')}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Booking Section */}
        <div className="booking-details-sidebar">
          <div className="details-booking-card">
            <h2 lang={currentLanguage}>{t('workshop_details.reserve_your_spot')}</h2>
            <div className="details-price-tag" itemProp="offers" itemScope itemType="https://schema.org/Offer">
              <meta itemProp="priceCurrency" content="EUR" />
              <span itemProp="price">{price ? `${price}€` : t('workshop_details.free_workshop')}</span>
              <link itemProp="availability" href={available_spots > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut"} />
            </div>
            
            <div className="details-booking-details">
              <div className="details-booking-detail">
                <i className="fa fa-calendar" aria-hidden="true"></i>
                <span itemProp="startDate">{formatDate(start_time)}</span>
              </div>
              <div className="details-booking-detail">
                <i className="fa fa-clock" aria-hidden="true"></i>
                <span>{formatTime(start_time)}</span>
              </div>
              <div className="details-booking-detail">
                <i className="fa fa-map-marker" aria-hidden="true"></i>
                <span itemProp="location">{location || t('workshop_details.main_studio')}</span>
              </div>
            </div>
            
            <div className="details-spots-remaining">
              <div 
                className="details-spots-indicator" 
                style={{ 
                  width: `${Math.max(0, Math.min(100, (available_spots / max_capacity) * 100))}%` 
                }}
                aria-hidden="true"
              ></div>
            </div>
            
            <div className="details-availability-counter" aria-live="polite">
              <span className="availability-number">{available_spots}</span>
              <span className="availability-divider">/</span>
              <span className="availability-max">{max_capacity}</span>
              <span className="availability-label" lang={currentLanguage}>{t('workshop_details.spots_available')}</span>
            </div>
            
            {/* Prepayment message box - only displayed if prepayment is not null */}
            {prepayment && (
              <div className="details-prepayment-box">
                <div className="details-prepayment-icon">
                  <i className="fa fa-info-circle" aria-hidden="true"></i>
                </div>
                <div className="details-prepayment-text">
                  {prepayment}
                </div>
              </div>
            )}
            
            <button 
              className="details-book-now-button"
              onClick={handleBookNowClick}
              disabled={available_spots <= 0}
              aria-label={isSlovak ? 
                available_spots > 0 ? `Rezervova� workshop ${title}` : "Tento workshop je plne obsaden�" : 
                available_spots > 0 ? `Book workshop ${title}` : "This workshop is fully booked"
              }
              lang={currentLanguage}
            >
              {available_spots > 0 ? t('workshop_details.book_now') : t('workshop_details.fully_booked')}
            </button>
            
            <div className="details-booking-policies">
              <details>
                <summary lang={currentLanguage}>{t('workshop_details.cancellation_policy')}</summary>
                <p lang={currentLanguage}>{t('workshop_details.cancellation_policy_text')}</p>
              </details>
            </div>
          </div>
          
          <div className="details-share-card">
            <h3 lang={currentLanguage}>{t('workshop_details.share_this_workshop')}</h3>
            <div className="details-share-buttons">
              <button 
                className="details-share-button details-facebook"
                onClick={shareOnFacebook}
                aria-label={t('workshop_details.share_on_facebook')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="16" height="16" fill="currentColor" aria-hidden="true">
                  <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"/>
                </svg>
              </button>
              <button 
                className="details-share-button details-twitter"
                onClick={shareOnTwitter}
                aria-label={t('workshop_details.share_on_twitter')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"/>
                </svg>
              </button>
              <button 
                className="details-share-button details-whatsapp"
                onClick={shareOnWhatsApp}
                aria-label={t('workshop_details.share_on_whatsapp')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                </svg>
              </button>
              <button 
                className="details-share-button details-email"
                onClick={shareViaEmail}
                aria-label={t('workshop_details.share_via_email')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal 
          yogaClass={bookingClass}
          onClose={handleBookingClose}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Booking Success Modal */}
      {bookingSuccess && bookingData && (
        <BookingSuccess 
          bookingData={bookingData}
          onClose={handleSuccessClose}
        />
      )}
    </div>
  );
};

export default WorkshopDetails;

